import type {
  CourtRank,
  Decan,
  Element,
  QabalisticWorld,
  Suit,
  TarotCard,
} from '@lilith-arcana-os/domain';
import { TarotCardSchema } from '@lilith-arcana-os/domain';

import {
  buildDecan,
  COURT_PROFILES,
  fitMajorColor,
  MINOR_CARD_NAMES,
  MINOR_DECANS,
  mirrorCardId,
  NUMBER_PROFILES,
  QLIPHAH_BY_SEPHIRAH,
  SEPHIROTH_BY_NUMBER,
  SUIT_PROFILES,
  uniq,
  ZODIAC_MODALITY_BY_SIGN,
  type MajorCardSeed,
} from './shared';

const MINOR_ID_START: Record<Suit, number> = {
  Bastoni: 23,
  Coppe: 37,
  Spade: 51,
  Denari: 65,
};

const COURT_ID_OFFSET: Record<CourtRank, number> = {
  Re: 10,
  Regina: 11,
  Cavaliere: 12,
  Fante: 13,
};

const ELEMENT_NOTES: Record<Element, string> = {
  Fuoco: 'si muove come una fiamma che cerca forma',
  Acqua: 'dilata il sentire e lo rende ambiente',
  Aria: 'taglia, collega e rimette in discussione',
  Terra: 'incarna, misura e rende visibile',
};

const worldPhrase = (world: QabalisticWorld): string => {
  switch (world) {
    case 'Atziluth':
      return 'nel mondo archetipico di Atziluth';
    case 'Briah':
      return 'nel mondo emotivo e creativo di Briah';
    case 'Yetzirah':
      return 'nel mondo mentale di Yetzirah';
    case 'Assiah':
    default:
      return 'nel mondo concreto di Assiah';
  }
};

const suitShadowPhrase = (suit: Suit): string => {
  switch (suit) {
    case 'Bastoni':
      return 'trasforma lo slancio in volontà di dominio e in fretta cieca';
    case 'Coppe':
      return 'spinge verso fusione, rimpianto e attaccamento al miraggio';
    case 'Spade':
      return 'indurisce il pensiero fino alla ferita e all isolamento';
    case 'Denari':
    default:
      return 'riduce il valore al possesso e il corpo a pura funzione';
  }
};

const buildMinorKeywords = (suit: Suit, number: number, decan: Decan | null): string[] => {
  const profile = SUIT_PROFILES[suit];
  const numberProfile = NUMBER_PROFILES[number as keyof typeof NUMBER_PROFILES];

  return uniq([
    numberProfile.keywords[0],
    numberProfile.keywords[1],
    numberProfile.keywords[2],
    profile.love_focus.split(',')[0] ?? profile.love_focus,
    decan?.zodiac_sign ?? profile.element,
  ]).slice(0, 5);
};

const buildMinorReversedKeywords = (suit: Suit, number: number): string[] => {
  const profile = SUIT_PROFILES[suit];
  const numberProfile = NUMBER_PROFILES[number as keyof typeof NUMBER_PROFILES];

  return uniq([
    numberProfile.shadow.split(' ')[0] ?? 'ombra',
    profile.shadow_focus.split(',')[0] ?? profile.shadow_focus,
    `eccesso di ${profile.element.toLowerCase()}`,
    'distorsione',
  ]).slice(0, 4);
};

const buildMinorNarratives = (suit: Suit, number: number, decan: Decan | null) => {
  const profile = SUIT_PROFILES[suit];
  const numberProfile = NUMBER_PROFILES[number as keyof typeof NUMBER_PROFILES];
  const sephirah = SEPHIROTH_BY_NUMBER[number];
  const signText = decan ? `${decan.chaldean_ruler} in ${decan.zodiac_sign}` : `la radice di ${profile.element.toLowerCase()}`;

  return {
    meaning_up: `${MINOR_CARD_NAMES[number as keyof typeof MINOR_CARD_NAMES]} di ${suit} apre ${numberProfile.archetype} ${worldPhrase(profile.world)}. ${profile.element} ${ELEMENT_NOTES[profile.element]}, e qui ${numberProfile.uplift}. ${signText} rende la carta concreta, situata, leggibile.`,
    meaning_rev: `Rovescio, ${MINOR_CARD_NAMES[number as keyof typeof MINOR_CARD_NAMES]} di ${suit} mostra come ${numberProfile.shadow}. La corrente del ${profile.element.toLowerCase()} si increspa: ciò che doveva muovere o nutrire si storce in compensazione. La carta chiede di ridare forma al centro prima di spingere ancora.`,
    meaning_love: `In amore parla di ${profile.love_focus}. Quando è dritta, invita a dare un gesto coerente al sentimento; quando è ferita, segnala che il non detto o l eccesso di intensita stanno spostando l equilibrio.`,
    meaning_work: `Nel lavoro insiste su ${profile.work_focus}. Mostra dove la spinta può portare risultato e dove, invece, il medesimo ritmo rischia di diventare consumo o conflitto.`,
    meaning_spirit: `Sul piano spirituale lavora su ${profile.spirit_focus}. ${sephirah} ricorda che non basta desiderare la soglia: bisogna attraversarla con pratica, misura e presenza.`,
    meaning_psyche: `Per la psiche mette in luce ${profile.psyche_focus}. Rivela la postura automatica che si attiva quando la persona cerca di proteggere il proprio nucleo o di non sentire il punto vivo.`,
    lilith_voice: `Io sono ${MINOR_CARD_NAMES[number as keyof typeof MINOR_CARD_NAMES].toLowerCase()} di ${suit.toLowerCase()}: apro il varco dove ${profile.shadow_focus}. Se mi rifiuti, brucio o ristagno sotto pelle; se mi guardi senza ornamento, divento potere lucidissimo.`,
    qliphotic_meaning: `Nella sua ombra, questa carta ascolta ${suitShadowPhrase(suit)}. La controparte qliphothica di ${sephirah} ricorda che ogni dono del seme contiene anche il rischio di un uso idolatrico della forza.`,
    simbologia: `${number} ${profile.symbol === 'bastone' ? 'bastoni' : profile.symbol === 'coppa' ? 'coppe' : profile.symbol === 'spada' ? 'spade' : 'denari'} emergono in ${profile.landscape}; la scena suggerisce ${numberProfile.archetype} più che semplice decorazione.`,
    numerology_meaning: numberProfile.numerology,
  };
};

const buildCourtNarratives = (suit: Suit, rank: CourtRank) => {
  const profile = SUIT_PROFILES[suit];
  const court = COURT_PROFILES[rank];

  return {
    meaning_up: `${rank} di ${suit} rappresenta una funzione incarnata: ${court.prompt}. Nel suo volto chiaro mostra ${court.keywords.join(', ')}, e insegna a trattare ${profile.element.toLowerCase()} come arte invece che come impulso grezzo.`,
    meaning_rev: `Rovescio, ${rank} di ${suit} perde misura. La stessa dote si piega in caricatura: autorita che soffoca, cura che trattiene, movimento che scappa o apprendistato che si sabota.`,
    meaning_love: `In amore agisce come una figura di stile relazionale. Può indicare una persona, ma più spesso segnala il modo in cui si entra nel legame: guida, ascolto, esplorazione o apprendistato emotivo.`,
    meaning_work: `Nel lavoro parla di ruolo, tono di leadership e capacità di presidiare il proprio campo. Mostra quale qualità va portata al tavolo e quale maschera va lasciata cadere.`,
    meaning_spirit: `Sul piano spirituale è una maschera iniziatica. Insegna come la corrente del seme attraversa il carattere e come il corpo può diventare strumento di trasmissione.`,
    meaning_psyche: `Per la psiche evidenzia la postura dell io quando deve scegliere se proteggere, esporre, guidare o imparare. È una carta utile per leggere dinamiche di maturazione identitaria.`,
    lilith_voice: `Io porto la nobiltà segreta di ${suit.toLowerCase()}. Se mi chiami soltanto per sembrare forte, ti mostro il vuoto del ruolo; se mi lasci scavare, ti rendo piu vero del titolo che indossi.`,
    qliphotic_meaning: `Nell ombra, la figura di corte usa il seme come costume. Invece di servire la corrente, la possiede e la irrigidisce in status, fascinazione o recita difensiva.`,
    simbologia: `${rank} di ${suit} appare dentro ${profile.landscape}, con l emblema del seme tenuto in modo consapevole: non un ornamento, ma un patto con la propria funzione.`,
    numerology_meaning: `Le carte di corte non contano una quantità: mostrano un modo di abitare la forza. ${rank} di ${suit} insegna come il seme diventa persona, etica e stile.`,
  };
};

export function createMajorArcanaCard(seed: MajorCardSeed): TarotCard {
  return TarotCardSchema.parse({
    id: seed.id,
    name: seed.name,
    slug: seed.slug,
    arcana: 'major',
    hermetic_title: seed.hermetic_title,
    suit: null,
    rank: null,
    number: seed.number,
    court: false,
    element: seed.element,
    element_secondary: null,
    qabalistic_world: null,
    sephirah: null,
    path_number: seed.path_number,
    hebrew_letter: seed.hebrew_letter,
    hebrew_char: seed.hebrew_char,
    tree_position: seed.tree_position,
    qliphah: seed.qliphah,
    tunnel_of_set: null,
    qliphotic_meaning: seed.qliphotic_meaning,
    astro_primary: seed.astro_primary,
    zodiac_sign: seed.zodiac_sign,
    zodiac_modality: seed.zodiac_modality,
    decan: null,
    chaldean_mirror_card_id: null,
    angels_day: null,
    angels_night: null,
    goetia_shadow: null,
    gemstone: seed.gemstone,
    incense: seed.incense,
    color_primary: seed.color_primary || fitMajorColor(seed.astro_primary),
    body_part: seed.body_part,
    musical_note: seed.musical_note,
    keywords_up: seed.keywords_up,
    keywords_rev: seed.keywords_rev,
    meaning_up: seed.meaning_up,
    meaning_rev: seed.meaning_rev,
    meaning_love: seed.meaning_love,
    meaning_work: seed.meaning_work,
    meaning_spirit: seed.meaning_spirit,
    meaning_psyche: seed.meaning_psyche,
    lilith_voice: seed.lilith_voice,
    lilith_qliphah_link: seed.lilith_qliphah_link,
    simbologia: seed.simbologia,
    symbol_emoji: seed.symbol_emoji,
    numerology_value: seed.number,
    numerology_meaning: seed.numerology_meaning,
    dignity: seed.dignity ?? 'neutral',
    solar_dates: seed.solar_dates,
  });
}

export function createMinorSuitCore(suit: Suit): TarotCard[] {
  const profile = SUIT_PROFILES[suit];
  const cards: TarotCard[] = [];
  const startId = MINOR_ID_START[suit];

  for (let number = 1; number <= 10; number += 1) {
    const pipNumber = number as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
    const cardId = startId + (number - 1);
    const decanSeed = number >= 2 ? MINOR_DECANS[suit][number - 2] ?? null : null;
    const hermeticTitle = profile.pip_titles[pipNumber] ?? profile.pip_titles[1] ?? 'Arcano minore';
    const decan = decanSeed
      ? buildDecan(
          decanSeed,
          cardId,
          hermeticTitle,
          profile.landscape,
        )
      : null;
    const sephirah = SEPHIROTH_BY_NUMBER[pipNumber] ?? 'Kether';
    const qliphah = QLIPHAH_BY_SEPHIRAH[sephirah];
    const narratives = buildMinorNarratives(suit, pipNumber, decan);

    cards.push(
      TarotCardSchema.parse({
        id: cardId,
        name: `${MINOR_CARD_NAMES[pipNumber]} di ${suit}`,
        slug: `${MINOR_CARD_NAMES[pipNumber].toLowerCase()}-di-${suit.toLowerCase()}`,
        arcana: 'minor',
        hermetic_title: hermeticTitle,
        suit,
        rank: null,
        number: pipNumber,
        court: false,
        element: profile.element,
        element_secondary: null,
        qabalistic_world: profile.world,
        sephirah,
        path_number: null,
        hebrew_letter: null,
        hebrew_char: null,
        tree_position: `${sephirah} in ${profile.world}`,
        qliphah,
        tunnel_of_set: null,
        qliphotic_meaning: narratives.qliphotic_meaning,
        astro_primary: decan ? `${decan.chaldean_ruler} in ${decan.zodiac_sign}` : `Radice di ${profile.element}`,
        zodiac_sign: decan?.zodiac_sign ?? null,
        zodiac_modality: decan ? ZODIAC_MODALITY_BY_SIGN[decan.zodiac_sign] : null,
        decan,
        chaldean_mirror_card_id: mirrorCardId(cardId),
        angels_day: decan?.angels_shemhamphorash.day.name ?? null,
        angels_night: decan?.angels_shemhamphorash.night.name ?? null,
        goetia_shadow: decan ? `Ombra del decano di ${decan.zodiac_sign}` : null,
        gemstone: profile.gemstone,
        incense: profile.incense,
        color_primary: profile.color,
        body_part: decan?.body_region ?? profile.body_focus,
        musical_note: profile.element === 'Fuoco' ? 'Do' : profile.element === 'Acqua' ? 'Re' : profile.element === 'Aria' ? 'Mi' : 'Fa',
        keywords_up: buildMinorKeywords(suit, pipNumber, decan),
        keywords_rev: buildMinorReversedKeywords(suit, pipNumber),
        meaning_up: narratives.meaning_up,
        meaning_rev: narratives.meaning_rev,
        meaning_love: narratives.meaning_love,
        meaning_work: narratives.meaning_work,
        meaning_spirit: narratives.meaning_spirit,
        meaning_psyche: narratives.meaning_psyche,
        lilith_voice: narratives.lilith_voice,
        lilith_qliphah_link: qliphah,
        simbologia: narratives.simbologia,
        symbol_emoji: profile.symbol_emoji,
        numerology_value: pipNumber,
        numerology_meaning: narratives.numerology_meaning,
        dignity: 'neutral',
        solar_dates: decan?.dates ?? null,
      }),
    );
  }

  (['Re', 'Regina', 'Cavaliere', 'Fante'] as const satisfies readonly CourtRank[]).forEach((rank) => {
    const cardId = startId + COURT_ID_OFFSET[rank];
    const court = COURT_PROFILES[rank];
    const narratives = buildCourtNarratives(suit, rank);

    cards.push(
      TarotCardSchema.parse({
        id: cardId,
        name: `${rank} di ${suit}`,
        slug: `${rank.toLowerCase()}-di-${suit.toLowerCase()}`,
        arcana: 'minor',
        hermetic_title: `${rank} del seme di ${suit}`,
        suit,
        rank,
        number: rank === 'Re' ? 11 : rank === 'Regina' ? 12 : rank === 'Cavaliere' ? 13 : 14,
        court: true,
        element: profile.element,
        element_secondary: court.element_secondary,
        qabalistic_world: profile.world,
        sephirah: null,
        path_number: null,
        hebrew_letter: null,
        hebrew_char: null,
        tree_position: `${rank} come figura di ${profile.world}`,
        qliphah: null,
        tunnel_of_set: null,
        qliphotic_meaning: narratives.qliphotic_meaning,
        astro_primary: `${court.astro_primary} di ${profile.element.toLowerCase()}`,
        zodiac_sign: null,
        zodiac_modality: null,
        decan: null,
        chaldean_mirror_card_id: mirrorCardId(cardId),
        angels_day: null,
        angels_night: null,
        goetia_shadow: null,
        gemstone: profile.gemstone,
        incense: profile.incense,
        color_primary: profile.color,
        body_part: profile.body_focus,
        musical_note: rank === 'Re' ? 'Sol' : rank === 'Regina' ? 'La' : rank === 'Cavaliere' ? 'Si' : 'Do',
        keywords_up: uniq([...court.keywords, profile.element.toLowerCase(), suit.toLowerCase()]).slice(0, 5),
        keywords_rev: uniq(['distorsione', profile.shadow_focus.split(',')[0] ?? profile.shadow_focus, rank.toLowerCase(), 'eccesso']).slice(0, 4),
        meaning_up: narratives.meaning_up,
        meaning_rev: narratives.meaning_rev,
        meaning_love: narratives.meaning_love,
        meaning_work: narratives.meaning_work,
        meaning_spirit: narratives.meaning_spirit,
        meaning_psyche: narratives.meaning_psyche,
        lilith_voice: narratives.lilith_voice,
        lilith_qliphah_link: null,
        simbologia: narratives.simbologia,
        symbol_emoji: profile.symbol_emoji,
        numerology_value: rank === 'Re' ? 14 : rank === 'Regina' ? 13 : rank === 'Cavaliere' ? 12 : 11,
        numerology_meaning: narratives.numerology_meaning,
        dignity: 'neutral',
        solar_dates: null,
      }),
    );
  });

  return cards;
}
